import { useEffect, useState } from "react";
import api, { fmtDate, fmtNZD } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const TABS = [
  ["projects", "Projects"],
  ["variations", "Variations"],
  ["purchase_orders", "Purchase Orders"],
  ["documents", "Documents"],
  ["invoices", "Invoices"],
];

export default function DeletedProjects() {
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [trash, setTrash] = useState({ variations: [], purchase_orders: [], documents: [], invoices: [] });
  const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  const [confirm, setConfirm] = useState(null);

  async function load() {
    setErr("");
    try {
      const [{ data: p }, { data: t }] = await Promise.all([
        api.get("/projects-deleted"),
        api.get("/trash"),
      ]);
      setProjects(p); setTrash(t);
    } catch (e) { setErr(e?.response?.data?.detail || "Load failed"); }
  }
  useEffect(() => { load(); }, []);

  async function restoreProject(p) {
    setMsg(""); setErr("");
    try { await api.post(`/projects/${p.id}/restore`); setMsg(`Restored ${p.name}`); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Restore failed"); }
  }
  async function restoreItem(path, id, label) {
    setMsg(""); setErr("");
    try { await api.post(`/${path}/${id}/restore`); setMsg(`Restored ${label}`); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Restore failed"); }
  }
  async function doPermanent() {
    if (!confirm) return;
    setConfirm({ ...confirm, busy: true });
    try {
      await api.delete(`/projects/${confirm.project.id}/permanent`, { data: { confirm_name: confirm.typed } });
      setMsg(`Permanently removed ${confirm.project.name}`);
      setConfirm(null); load();
    } catch (e) {
      setConfirm({ ...confirm, busy: false, err: e?.response?.data?.detail || "Delete failed" });
    }
  }
  function daysLeft(deletedAt) {
    try {
      const d = new Date(deletedAt);
      const purge = new Date(d.getTime() + 30 * 86400000);
      return Math.max(0, Math.ceil((purge - Date.now()) / 86400000));
    } catch { return 30; }
  }

  return (
    <div className="space-y-3" data-testid="deleted-items-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl">Deleted items</h1>
        <button className="btn-ghost text-sm" onClick={() => nav("/projects")} data-testid="btn-back-to-projects">← Back to Projects</button>
      </div>
      <p className="text-sm opacity-70">Everything in the Deleted bin auto-purges 30 days after deletion. Admin can Restore or Delete permanently (projects only).</p>
      {err && <div className="text-red-500 text-sm">{err}</div>}
      {msg && <div className="text-green-400 text-sm" data-testid="deleted-items-msg">{msg}</div>}

      <div className="flex gap-2 flex-wrap">
        {TABS.map(([k, l]) => {
          const n = k === "projects" ? projects.length
                  : k === "variations" ? trash.variations.length
                  : k === "purchase_orders" ? trash.purchase_orders.length
                  : k === "invoices" ? (trash.invoices || []).length
                  : trash.documents.length;
          return (
            <button key={k} data-testid={`dtab-${k}`} onClick={() => setTab(k)}
                    className={`px-3 py-1.5 text-sm rounded ${tab===k?"bg-silver text-black":"text-white border border-neutral-800"}`}>
              {l} <span className="opacity-60 ml-1">({n})</span>
            </button>
          );
        })}
      </div>

      {tab === "projects" && (
        <div className="card">
          <table className="table">
            <thead><tr><th>Name</th><th>Client</th><th>Deleted</th><th>Days left</th><th>Contract</th><th></th></tr></thead>
            <tbody>
              {projects.map(p => (
                <tr key={p.id} data-testid={`deleted-row-${p.id}`}>
                  <td className="text-silver">{p.name}</td>
                  <td>{p.client_name || "—"}</td>
                  <td>{fmtDate(p.deleted_at)}</td>
                  <td>{daysLeft(p.deleted_at)}</td>
                  <td>{fmtNZD(p.contract_amount)}</td>
                  <td className="flex gap-2 text-xs">
                    <button className="hub-link" onClick={() => restoreProject(p)} data-testid={`btn-restore-${p.id}`}>Restore</button>
                    <button className="text-red-400" onClick={() => setConfirm({ project: p, typed: "", busy: false })} data-testid={`btn-perm-delete-${p.id}`}>Delete permanently</button>
                  </td>
                </tr>
              ))}
              {projects.length === 0 && <tr><td colSpan="6" className="opacity-60">The Deleted bin is empty.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "variations" && (
        <div className="card">
          <table className="table">
            <thead><tr><th>No.</th><th>Description</th><th>Amount ex GST</th><th>Status</th><th>Deleted</th><th>Days left</th><th></th></tr></thead>
            <tbody>
              {trash.variations.map(v => (
                <tr key={v.id} data-testid={`deleted-var-${v.id}`}>
                  <td>{v.number}</td><td>{v.description}</td><td>{fmtNZD(v.amount_ex)}</td>
                  <td><span className="badge badge-grey">{v.status}</span></td>
                  <td>{fmtDate(v.deleted_at)}</td>
                  <td>{daysLeft(v.deleted_at)}</td>
                  <td><button className="hub-link text-xs" onClick={() => restoreItem("variations", v.id, v.number)} data-testid={`btn-restore-var-${v.id}`}>Restore</button></td>
                </tr>
              ))}
              {trash.variations.length === 0 && <tr><td colSpan="7" className="opacity-60">No deleted variations.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "purchase_orders" && (
        <div className="card">
          <table className="table">
            <thead><tr><th>No.</th><th>Supplier</th><th>Total</th><th>Status</th><th>Deleted</th><th>Days left</th><th></th></tr></thead>
            <tbody>
              {trash.purchase_orders.map(p => (
                <tr key={p.id} data-testid={`deleted-po-${p.id}`}>
                  <td>{p.number}</td><td>{p.supplier_name}</td><td>{fmtNZD(p.total_ex)}</td>
                  <td><span className="badge badge-grey">{p.status}</span></td>
                  <td>{fmtDate(p.deleted_at)}</td>
                  <td>{daysLeft(p.deleted_at)}</td>
                  <td><button className="hub-link text-xs" onClick={() => restoreItem("purchase-orders", p.id, p.number)} data-testid={`btn-restore-po-${p.id}`}>Restore</button></td>
                </tr>
              ))}
              {trash.purchase_orders.length === 0 && <tr><td colSpan="7" className="opacity-60">No deleted purchase orders.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "documents" && (
        <div className="card">
          <table className="table">
            <thead><tr><th>Name</th><th>Folder</th><th>Deleted</th><th>Days left</th><th></th></tr></thead>
            <tbody>
              {trash.documents.map(d => (
                <tr key={d.id} data-testid={`deleted-doc-${d.id}`}>
                  <td>{d.name || d.filename}</td><td>{d.folder}</td>
                  <td>{fmtDate(d.deleted_at)}</td>
                  <td>{daysLeft(d.deleted_at)}</td>
                  <td><button className="hub-link text-xs" onClick={() => restoreItem("documents", d.id, d.name || d.filename)} data-testid={`btn-restore-doc-${d.id}`}>Restore</button></td>
                </tr>
              ))}
              {trash.documents.length === 0 && <tr><td colSpan="5" className="opacity-60">No deleted documents.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "invoices" && (
        <div className="card">
          <table className="table">
            <thead><tr><th>Client</th><th>Project</th><th>Amount ex GST</th><th>Deleted</th><th>Days left</th><th></th></tr></thead>
            <tbody>
              {(trash.invoices || []).map(i => (
                <tr key={i.id} data-testid={`deleted-inv-${i.id}`}>
                  <td>{i.client_name || "—"}</td><td>{i.project_name || "—"}</td><td>{fmtNZD(i.amount_ex)}</td>
                  <td>{fmtDate(i.deleted_at)}</td>
                  <td>{daysLeft(i.deleted_at)}</td>
                  <td><button className="hub-link text-xs" onClick={() => restoreItem("invoices", i.id, `${i.client_name || "invoice"} draft`)} data-testid={`btn-restore-inv-${i.id}`}>Restore</button></td>
                </tr>
              ))}
              {(trash.invoices || []).length === 0 && <tr><td colSpan="6" className="opacity-60">No deleted invoices.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4" data-testid="perm-delete-dialog">
          <div className="card max-w-md w-full">
            <h3 className="text-silver mb-2">Delete permanently</h3>
            <p className="text-sm opacity-80 mb-3">Permanently remove <b>{confirm.project.name}</b> and all linked records? This cannot be undone.</p>
            <label className="text-xs block">Type <b className="text-silver">{confirm.project.name}</b> to confirm
              <input className="input mt-1" value={confirm.typed} onChange={e => setConfirm({ ...confirm, typed: e.target.value })} data-testid="perm-typed-name"/>
            </label>
            {confirm.err && <div className="text-red-500 text-sm mt-2" data-testid="perm-error">{confirm.err}</div>}
            <div className="flex gap-2 mt-4">
              <button className={`px-3 py-1.5 rounded text-sm ${confirm.typed.trim() === confirm.project.name.trim() ? "bg-red-600 text-white" : "bg-neutral-700 text-neutral-400 cursor-not-allowed"}`}
                      disabled={confirm.typed.trim() !== confirm.project.name.trim() || confirm.busy}
                      onClick={doPermanent} data-testid="perm-confirm">
                {confirm.busy ? "Deleting…" : "Delete permanently"}
              </button>
              <button className="btn-ghost" onClick={() => setConfirm(null)} data-testid="perm-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
